import type { User } from '@supabase/supabase-js';

/**
 * Resolution rules for the SIGNED-IN USER'S OWN avatar.
 *
 * The Google profile photo is the default avatar: a parent who signs in with
 * Google sees their own face without doing anything. Uploading an image or
 * picking an emoji is an explicit override and always wins.
 *
 * The "did they pick an emoji?" signal is `users.avatar_emoji IS NULL`. The
 * column has no DB default and both writers (onboarding's Profile Setup and
 * Settings → Profile) only send it once the user actually saves from the emoji
 * picker, so NULL genuinely means "never chose one" rather than "chose the
 * placeholder". That is what keeps this migration-free — and it is also why
 * existing beta users keep their current emoji: their rows already have a
 * value, so nothing changes under them.
 *
 * Only for the user's own avatar. Other people's avatars (children, household
 * members) resolve from their own stored fields; the inviter's photo on the
 * invite Acceptance screen is resolved server-side by `invitations-preview`,
 * which applies the same googleusercontent host check as below.
 */

/** Shown when there is neither an image, a chosen emoji, nor a Google photo. */
export const DEFAULT_AVATAR_EMOJI = '\u{1F984}';

/**
 * The Google profile photo on the Supabase session, or null.
 *
 * Supabase copies the OAuth `picture` claim into `user_metadata` as
 * `avatar_url` (older sessions carry `picture`), so both are read. The URL is
 * rendered directly in an <img>, so the host is pinned to Google's CDN —
 * user_metadata is attacker-influenceable on some providers and an unchecked
 * URL here would be an open image-loading redirect that also leaks the
 * viewer's IP to whatever host it names.
 */
export function googleAvatarUrlFrom(
  user: Pick<User, 'user_metadata'> | null | undefined,
): string | null {
  const metadata = user?.user_metadata;
  const candidate =
    typeof metadata?.avatar_url === 'string'
      ? metadata.avatar_url
      : typeof metadata?.picture === 'string'
        ? metadata.picture
        : null;
  if (!candidate) return null;

  try {
    const url = new URL(candidate);
    return url.protocol === 'https:' &&
      (url.hostname === 'googleusercontent.com' ||
        url.hostname.endsWith('.googleusercontent.com'))
      ? url.toString()
      : null;
  } catch {
    return null;
  }
}

export interface OwnAvatarInput {
  /** `users.avatar_image_path` — an uploaded image in the profile bucket. */
  avatarImagePath: string | null;
  /** `users.avatar_emoji` — NULL means the user never picked one. */
  avatarEmoji: string | null;
  googleAvatarUrl: string | null;
}

export interface OwnAvatarProps {
  imagePath: string | null;
  directUrl: string | null;
  emoji: string;
}

/**
 * Maps stored avatar fields onto `<ProfileAvatar>` props.
 *
 * Precedence: uploaded image → chosen emoji → Google photo → default emoji.
 *
 * `imagePath` and `directUrl` are deliberately never both set: ProfileAvatar
 * gives `directUrl` precedence (it exists for local blob previews), which is
 * the opposite of what we want, so the caller must not hand it both.
 */
export function ownAvatarProps({
  avatarImagePath,
  avatarEmoji,
  googleAvatarUrl,
}: OwnAvatarInput): OwnAvatarProps {
  if (avatarImagePath) {
    return {
      imagePath: avatarImagePath,
      directUrl: null,
      emoji: avatarEmoji ?? DEFAULT_AVATAR_EMOJI,
    };
  }
  if (avatarEmoji) {
    return { imagePath: null, directUrl: null, emoji: avatarEmoji };
  }
  return {
    imagePath: null,
    directUrl: googleAvatarUrl,
    emoji: DEFAULT_AVATAR_EMOJI,
  };
}
